import { StandardEvents } from '@shopify/events';

class PPProductTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('change', this.syncState);
    this.syncState();
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('change', this.syncState);
  }

  get tabs() {
    return Array.from(this.querySelectorAll('.pp-product-tab__input'));
  }

  handleKeydown = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const tabs = this.tabs;
    const currentIndex = tabs.indexOf(event.target);
    if (currentIndex < 0) return;

    event.preventDefault();

    let nextIndex = currentIndex;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;

    tabs[nextIndex].checked = true;
    tabs[nextIndex].focus();
    tabs[nextIndex].dispatchEvent(new Event('change', { bubbles: true }));
  };

  syncState = () => {
    for (const input of this.tabs) {
      const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const panel = input.parentElement?.querySelector('.pp-product-tab__panel');
      label?.setAttribute('aria-selected', String(input.checked));
      panel?.setAttribute('aria-hidden', String(!input.checked));
    }
  };
}

if (!customElements.get('pp-product-tabs')) {
  customElements.define('pp-product-tabs', PPProductTabs);
}

/*
 * The thumbnail strip pages on desktop.
 *
 * A product can carry more photographs than the four the design shows at once,
 * so on wide screens the strip is a window two thumbnails square that turns to
 * whichever page holds the selected image. Below the gallery's desktop
 * breakpoint the element does nothing: the strip is the two-up scroller its own
 * stylesheet lays out, and the height and transform set here are cleared so
 * that layout is left untouched.
 *
 * The page is measured from the laid-out thumbnails rather than computed from
 * settings, so the gap and the responsive thumbnail size stay exact and a
 * resize simply re-measures. Slide changes arrive as the slideshow's own
 * `slideshow:select`, the same event its dots and arrows already raise; it
 * bubbles, so the listener sits on the slideshow the strip belongs to. The
 * active thumbnail's aria-selected — and thus which page a photograph is on —
 * is the slideshow's doing through the thumbnails ref, not ours.
 *
 * A fresh element is created whenever a variant swaps the gallery, so state
 * lives on the instance and the listeners are torn down on disconnect.
 */
const THUMBS_PER_PAGE = 4;
const GALLERY_DESKTOP = '(min-width: 1200px)';
const GALLERY_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

class PPGalleryThumbs extends HTMLElement {
  #page = 0;
  #desktop = matchMedia(GALLERY_DESKTOP);
  #reducedMotion = matchMedia(GALLERY_REDUCED_MOTION);
  #slideshow = null;
  #resize = new ResizeObserver(() => this.#render());

  connectedCallback() {
    this.#slideshow = this.closest('slideshow-component');
    this.#slideshow?.addEventListener('slideshow:select', this.#handleSelect);
    this.#desktop.addEventListener('change', this.#render);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.#resize.observe(this);

    // Open on the page holding whatever the slideshow already marks as current.
    const active = this.#thumbs.findIndex((thumb) => thumb.getAttribute('aria-selected') === 'true');
    this.#page = active > 0 ? Math.floor(active / THUMBS_PER_PAGE) : 0;

    // Thumbnails have no measured size until the first layout pass.
    requestAnimationFrame(this.#render);
  }

  disconnectedCallback() {
    this.#slideshow?.removeEventListener('slideshow:select', this.#handleSelect);
    this.#desktop.removeEventListener('change', this.#render);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.#resize.disconnect();
  }

  get #track() {
    return this.querySelector('.pp-product-gallery__thumbs-track');
  }

  get #thumbs() {
    return Array.from(this.querySelectorAll('.pp-product-gallery__thumb'));
  }

  #handleSelect = (event) => {
    const index = event.detail?.index;
    if (typeof index !== 'number') return;
    this.#page = Math.floor(index / THUMBS_PER_PAGE);
    this.#render();
    // On a phone the strip is a horizontal scroller, not a paged window, so the
    // active thumbnail has to be scrolled into view — the desktop #render above
    // does nothing there. Desktop is left entirely to the paging.
    if (!this.#desktop.matches) this.#revealThumb(index);
  };

  /*
   * Brings the active thumbnail into the mobile strip when the main image
   * changes — swipe, arrow or dot. Only the strip's own scroll is moved (rect
   * maths + scrollBy), so the page never jumps, and the thumbnail is aligned to
   * the strip's start edge, which is a snap point, so the strip's mandatory
   * x-snap does not fight the programmatic scroll. A thumbnail already fully in
   * view is left alone, so moving between the two on screen does not jump.
   */
  #revealThumb = (index) => {
    const thumb = this.#thumbs[index];
    if (!thumb) return;

    const strip = this.getBoundingClientRect();
    const rect = thumb.getBoundingClientRect();
    const fullyVisible = rect.left >= strip.left - 1 && rect.right <= strip.right + 1;
    if (fullyVisible) return;

    this.scrollBy({
      left: rect.left - strip.left,
      behavior: this.#reducedMotion.matches ? 'auto' : 'smooth',
    });
  };

  /*
   * A thumbnail reached by keyboard on a hidden page pulls its page into view,
   * so tabbing through the strip never leaves the focused thumbnail clipped.
   */
  #handleFocusIn = (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('.pp-product-gallery__thumb') : null;
    if (!thumb) return;
    const index = this.#thumbs.indexOf(thumb);
    if (index < 0) return;
    this.#page = Math.floor(index / THUMBS_PER_PAGE);
    this.#render();
  };

  #render = () => {
    const track = this.#track;
    if (!track) return;

    // Off desktop the stylesheet owns the layout; drop anything set here.
    if (!this.#desktop.matches) {
      this.style.removeProperty('height');
      track.style.removeProperty('transform');
      return;
    }

    const thumbs = this.#thumbs;
    if (thumbs.length === 0) return;

    // Two rows tall, measured from the first page so every page is the same
    // height. offsetTop is relative to the track, which is positioned on desktop.
    const lastOnFirstPage = thumbs[Math.min(THUMBS_PER_PAGE - 1, thumbs.length - 1)];
    this.style.height = `${lastOnFirstPage.offsetTop + lastOnFirstPage.offsetHeight}px`;

    // Slide the track so the target page's first row sits at the window's top.
    const firstOnPage = thumbs[this.#page * THUMBS_PER_PAGE];
    track.style.transform = firstOnPage ? `translateY(${-firstOnPage.offsetTop}px)` : 'translateY(0)';
  };
}

if (!customElements.get('pp-gallery-thumbs')) {
  customElements.define('pp-gallery-thumbs', PPGalleryThumbs);
}

/*
 * The hero background follows the chosen variant.
 *
 * The colour is a variant metafield rather than the swatch, so a dark product
 * cannot drag the section dark with it and take the copy along. Liquid resolves
 * it for the first paint, including a direct variant URL; this is only the
 * update when a shopper changes variant without a page load.
 *
 * The work is done by Horizon: `productSelect` carries a promise for the
 * re-rendered section, and the colour is simply read off the panel in that
 * answer. Nothing here fetches, and nothing here knows what the colour means.
 *
 * Bound to the document because the event bubbles that far — quick add already
 * listens there — and because the hero survives a variant change while its
 * inner components are replaced. The two guards below are what keep a document
 * wide listener honest.
 */
const HERO = '.pp-product-hero';
const HERO_BACKGROUND = '--pp-product-hero-background';

/*
 * The most recent selection per hero. Per hero rather than one counter for the
 * page, so two heroes on one page cannot cancel each other's updates.
 */
const latestSelection = new WeakMap();

/** @param {HTMLElement} hero @param {string} colour */
const applyHeroBackground = (hero, colour) => {
  if (!colour) return;

  hero.style.setProperty(HERO_BACKGROUND, colour);

  /*
   * And on the section wrapper, whose own value came from a style block the
   * server rendered once. A variant change replaces components rather than the
   * whole section, so that rule still holds the colour the page opened on; an
   * inline property outranks it, which is what makes this stick. Do not
   * "simplify" this to one call.
   */
  const section = hero.closest('.shopify-section');
  if (section instanceof HTMLElement) section.style.setProperty(HERO_BACKGROUND, colour);
};

document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target;

  // A swatch on a product card announces the same event and means nothing here.
  if (!(target instanceof Element) || target.closest('product-card')) return;

  const hero = target.closest(HERO);
  if (!(hero instanceof HTMLElement)) return;

  const token = (latestSelection.get(hero) ?? 0) + 1;
  latestSelection.set(hero, token);

  event.promise
    .then(({ detail }) => {
      // A slower earlier answer must not repaint over a newer choice.
      if (latestSelection.get(hero) !== token) return;

      const updated = detail?.html?.querySelector(HERO);
      if (!(updated instanceof HTMLElement)) return;

      applyHeroBackground(hero, updated.style.getPropertyValue(HERO_BACKGROUND).trim());
    })
    .catch(() => {
      // An aborted or failed variant request leaves the colour where it is.
    });
});

/*
 * A colour change keeps the page where it is.
 *
 * Nico, 2026-11-03: on desktop a swatch click threw the page "irgendwo" down.
 * From 990px the page scrolls inside `.page-wrapper` (assets/base.css), and a
 * variant change swaps most of the hero at once: the gallery replaces itself
 * (media-gallery.js), the buy box is morphed, images reload. The browser's
 * scroll anchoring then "corrects" the scroller against an anchor node that
 * was just replaced. The hero is taken out of anchor selection in
 * pp-product-hero.css (overflow-anchor: none); this holds the position for
 * the few frames the swap takes, on whichever element actually scrolls, and
 * lets go the moment the shopper scrolls themselves.
 */
const scrollers = () => {
  const list = [];
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight) {
    const overflow = getComputedStyle(wrapper).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') list.push(wrapper);
  }
  if (document.scrollingElement instanceof HTMLElement) list.push(document.scrollingElement);
  return list;
};

const HOLD_MS = 700;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target;
  if (!(target instanceof Element) || target.closest('product-card') || !target.closest(HERO)) return;

  const held = scrollers().map((element) => ({ element, top: element.scrollTop }));
  let released = false;
  const release = () => {
    released = true;
    USER_SCROLL_EVENTS.forEach((name) => window.removeEventListener(name, release, true));
  };
  // Listening starts after this click's own pointer events have passed.
  requestAnimationFrame(() => {
    if (!released) USER_SCROLL_EVENTS.forEach((name) => window.addEventListener(name, release, true));
  });

  const hold = () => {
    if (released) return;
    for (const { element, top } of held) {
      if (Math.abs(element.scrollTop - top) > 1) element.scrollTo({ top, behavior: 'instant' });
    }
  };

  const until = performance.now() + HOLD_MS;
  const loop = () => {
    hold();
    if (!released && performance.now() < until) requestAnimationFrame(loop);
    else release();
  };

  event.promise
    ?.then(() => {
      hold();
      requestAnimationFrame(loop);
    })
    .catch(() => release());
});
